import React, { useState } from 'react';
import { ConnectPaymentDetails } from '@stripe/react-connect-js';
import { ChevronRight } from 'lucide-react';
import Modal from '@/Components/Modal/Modal';
import { ContractDisputesBlock } from '@/features/contracts/components/ContractDisputesBlock';
import { disputeStatusInfo } from '@/features/contracts/constants/dispute-labels';
import {
  useContractPayments,
  type ContractListItem,
  type ContractPaymentItem,
} from '../hooks/useContractsPayments';
// Formatage partagé avec l'export de l'onglet Contrats : le CSV reprend ces valeurs.
import {
  beneficiaryName,
  contractStatusLabel,
  durationLabel,
  euroEur,
  fmtDate,
  fmtUnix,
  guarantorAddress,
  paidInstallments,
  paymentMode,
  subscriptionAmount,
  subscriptionStatusLabel,
} from '../utils/contractDetailFormat';

function invoiceStatus(s: string, refunded = false): { label: string; cls: string } {
  if (refunded) {
    return { label: 'Remboursé', cls: 'tw-bg-gray-100 tw-text-gray-600' };
  }
  switch (s) {
    case 'paid':
      return { label: 'Payé', cls: 'tw-bg-green-50 tw-text-green-700' };
    case 'open':
      return { label: 'En attente', cls: 'tw-bg-amber-50 tw-text-amber-700' };
    case 'uncollectible':
      return { label: 'Échec', cls: 'tw-bg-red-50 tw-text-red-700' };
    case 'void':
      return { label: 'Annulé', cls: 'tw-bg-gray-100 tw-text-gray-600' };
    case 'draft':
      return { label: 'Brouillon', cls: 'tw-bg-gray-100 tw-text-gray-600' };
    default:
      return { label: s, cls: 'tw-bg-gray-100 tw-text-gray-600' };
  }
}

/* ---------- petits composants d'affichage ---------- */

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="tw-flex tw-justify-between tw-gap-4 tw-py-1 tw-text-sm">
    <span className="tw-text-gray-500">{label}</span>
    <span className="tw-text-gray-900 tw-text-right tw-break-words">{value ?? '—'}</span>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
    <div className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">
      {title}
    </div>
    {children}
  </div>
);

/**
 * Modale détaillée d'un contrat : résumé enrichi (bénéficiaire / garant / véhicule /
 * garantie / souscription) + progression + liste des paiements. Au clic sur un
 * paiement → 2ᵉ modale rendant le composant Stripe natif `ConnectPaymentDetails`
 * (même vue que l'onglet Paiements, frais « de traitement » combinés).
 */
export const ContractDetailModal: React.FC<{
  contract: ContractListItem;
  onClose: () => void;
}> = ({ contract, onClose }) => {
  const { data, isLoading, isError } = useContractPayments(contract.id);
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);

  const c = data?.contract;
  const beneficiary = c?.beneficiary;
  const warranty = c?.warranty;
  const vehicle = c?.vehicle;
  const guarantor = c?.society; // garant (résolu par enrichContractData)
  const sub = data?.subscription;
  const progress = data?.progress;

  const renderPaymentRow = (p: ContractPaymentItem, idx: number) => {
    const st = invoiceStatus(p.status, p.refunded);
    const clickable = !!p.paymentId;
    const dispute = data?.disputes?.find((d) => d.chargeId === p.paymentId);
    return (
      <tr
        key={p.paymentId ?? `row-${idx}`}
        onClick={clickable ? () => setSelectedPayment(p.paymentId) : undefined}
        className={`tw-border-b tw-border-gray-100 last:tw-border-0 ${
          clickable ? 'tw-cursor-pointer hover:tw-bg-gray-50' : 'tw-opacity-70'
        }`}
      >
        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700">{fmtUnix(p.date)}</td>
        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700">
          {p.echeanceNumber != null ? `N° ${p.echeanceNumber}` : '—'}
        </td>
        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-right tw-tabular-nums tw-font-medium tw-text-gray-900">
          {euroEur(p.amount)}
        </td>
        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-right tw-tabular-nums tw-text-gray-500">
          {euroEur(p.combinedFee)}
        </td>
        <td className="tw-px-3 tw-py-2">
          <span
            className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${st.cls}`}
          >
            {st.label}
          </span>
          {dispute && (
            <span
              title={`Litige : ${disputeStatusInfo(dispute.status).label}`}
              className="tw-ml-1 tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-700"
            >
              Contesté
            </span>
          )}
        </td>
        <td className="tw-px-3 tw-py-2 tw-text-right">
          {clickable && <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400" />}
        </td>
      </tr>
    );
  };

  return (
    <>
      <Modal title={`Contrat ${contract.label}`} onRequestClose={onClose}>
        <div
          className="modal-body"
          style={{ width: 'min(920px, 94vw)', maxHeight: '82vh', overflowY: 'auto' }}
        >
          {isLoading ? (
            <div className="tw-p-6 tw-text-center tw-text-gray-500">Chargement…</div>
          ) : isError || !data ? (
            <div className="tw-p-6 tw-text-center tw-text-red-600">
              Impossible de charger le détail du contrat.
            </div>
          ) : (
            <div className="tw-space-y-4">
              {/* Résumé : cartes d'infos */}
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                <Card title="Bénéficiaire">
                  <InfoRow label="Nom" value={beneficiaryName(beneficiary)} />
                  <InfoRow label="E-mail" value={beneficiary?.email} />
                  <InfoRow label="Téléphone" value={beneficiary?.phoneNumber} />
                </Card>

                <Card title="Garant">
                  <InfoRow label="Société" value={guarantor?.name} />
                  <InfoRow label="E-mail" value={guarantor?.email} />
                  <InfoRow label="Adresse" value={guarantorAddress(guarantor)} />
                </Card>

                <Card title="Véhicule">
                  <InfoRow label="Immatriculation" value={vehicle?.registration} />
                  <InfoRow label="Marque" value={vehicle?.brand} />
                  <InfoRow label="Modèle" value={vehicle?.model} />
                  <InfoRow label="Carburant" value={vehicle?.fuel} />
                </Card>

                <Card title="Garantie">
                  <InfoRow label="Produit" value={warranty?.name} />
                  <InfoRow label="Durée" value={durationLabel(c?.duration)} />
                  <InfoRow label="Statut contrat" value={contractStatusLabel(c?.idStatus)} />
                </Card>
              </div>

              {/* Souscription + progression */}
              <Card title="Souscription">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-6">
                  <div>
                    <InfoRow label="Mode" value={paymentMode(c?.monthlyPaymentId)} />
                    <InfoRow label="Montant" value={subscriptionAmount(c)} />
                    <InfoRow label="Date de création" value={fmtDate(c?.dateCreation)} />
                    <InfoRow label="Date de souscription" value={fmtDate(c?.dateSouscription)} />
                  </div>
                  <div>
                    <InfoRow label="Statut souscription" value={subscriptionStatusLabel(sub)} />
                    <InfoRow label="Prochaine échéance" value={fmtUnix(sub?.nextPaymentDate)} />
                    <InfoRow label="Échéances payées" value={paidInstallments(progress)} />
                    <InfoRow
                      label="Montant restant"
                      value={euroEur(progress?.remainingAmount)}
                    />
                  </div>
                </div>
                {progress && progress.totalAmountTTC > 0 && (
                  <div className="tw-mt-2 tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded tw-overflow-hidden">
                    <div
                      className="tw-h-2 tw-bg-emerald-500"
                      style={{ width: `${Math.min(100, progress.progressPercent)}%` }}
                    />
                  </div>
                )}
              </Card>

              <ContractDisputesBlock disputes={data.disputes} />

              {/* Liste des paiements */}
              <div>
                <div className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">
                  Paiements
                </div>
                <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                  <table className="tw-min-w-full">
                    <thead>
                      <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
                        <th className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-medium tw-text-gray-500">
                          Date
                        </th>
                        <th className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-medium tw-text-gray-500">
                          Échéance
                        </th>
                        <th className="tw-px-3 tw-py-2 tw-text-right tw-text-[11px] tw-font-medium tw-text-gray-500">
                          Montant
                        </th>
                        <th className="tw-px-3 tw-py-2 tw-text-right tw-text-[11px] tw-font-medium tw-text-gray-500">
                          Frais
                        </th>
                        <th className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-medium tw-text-gray-500">
                          Statut
                        </th>
                        <th className="tw-px-3 tw-py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-gray-500"
                          >
                            Aucun paiement pour ce contrat.
                          </td>
                        </tr>
                      ) : (
                        data.payments.map(renderPaymentRow)
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="tw-mt-1 tw-text-xs tw-text-gray-400">
                  Cliquez sur un paiement pour voir le détail (reçu, moyen de paiement, chronologie).
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {selectedPayment && (
        <Modal title="Détail du paiement" onRequestClose={() => setSelectedPayment(null)}>
          <div
            className="modal-body"
            style={{ width: 'min(780px, 94vw)', maxHeight: '82vh', overflowY: 'auto' }}
          >
            <ConnectPaymentDetails
              payment={selectedPayment}
              onClose={() => setSelectedPayment(null)}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
